import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircleIcon, MessageCircle, Mail, Bell, StickyNote, FileText,
  LayoutGrid, Calendar, Target, Folder, BookOpen, Network, Settings,
  type LucideIcon,
} from '../../icons';
import { useNotificationStore } from '../../store/notificationStore';
import { useChatStore } from '../../store/chatStore';
import { useMemoPanelStore } from '../../store/memoPanelStore';
import { MEMO_PANEL_ENABLED } from '../../lib/memo-flag';
import { tMemo } from '../../lib/memo-i18n';
import { NotificationPanel } from '../NotificationPanel';
import { ChatDropdown } from '../ChatDropdown';
import { StatusDropdown, statusLabel } from '../StatusDropdown';
import { useWorkStatusStore } from '../../store/workStatusStore';
import { WORK_STATUS_TONE } from '../../lib/work-status';
import { formatSessionTime, useSessionIdle } from '../../lib/session-idle';
import { tLayout } from '../../lib/layout-i18n';
import { useMenuConfig, findMenu, isMenuVisible } from '../../lib/menu-config';
import './StatusBar.css';

/**
 * ⑥ 하단 상태바(VS Code형) — 설계 §5-⑥. TopBar 를 대체(design-20260717 Phase 2).
 * docs/design/design-20260717-common-layout-skeleton.md
 *
 * 좌: 상태 · 채팅(미읽음) · 메일 · 알림 · 전자결재. 중앙: 퀵바(Ctrl+Space). 우: 세션.
 *
 * ⚠ 알림/미읽음 SSE 구독은 `LayoutNotificationHost`(AppLayout 레벨)가 단독 소유(Phase 2-A).
 * StatusBar 는 store 를 **읽어 표시만** 하고, 알림 벨은 store 를 읽어 배지 + 드롭다운(위로 열림)만 연다.
 *
 * ★ 2026-08-11 (사용자 요구) — **상태바 메뉴도 관리 콘솔 "메뉴 관리"가 기준**이다.
 *   좌측 사이드바와 같은 레지스트리(`/menu-config` → `admin_menu_registry`)를 읽어, 관리자가
 *   끈 메뉴는 상태바에서도 사라진다. 항목별 `menuKey` 로 레지스트리 행과 짝지으며 라벨·이동
 *   경로도 레지스트리 값을 따른다(관리자가 URL 을 바꾸면 상태바도 따라간다).
 *   - 레지스트리 응답에는 **활성 행만** 들어온다 → "행이 있다 = 노출".
 *   - 조회 실패/미구성이면 지금까지처럼 전부 노출(fail-open) — 화면이 통째로 비는 것을 막는다.
 *     데이터 차단은 서버(chat-server `menu-registry-guard`)가 별도로 강제하므로 안전하다.
 *   - `menuKey` 가 없는 항목(상태·알림·메모·퀵바·세션)은 메뉴가 아니라 시스템 UI 라 상시 표시.
 *
 * ★ 채팅 항목은 **드롭다운**이다(사용자 요구): 누르면 알림 내역과 같은 방식으로 대화 내역이
 *   열리고, 항목을 고르면 그 방으로 이동한다. 기존의 "바로 /chat 이동"은 드롭다운 하단
 *   '전체 보기'가 승계한다. 내역을 고르면 그 방의 숫자(배지)는 그 자리에서 사라진다
 *   (ChatDropdown 이 낙관적으로 지운다 — 2026-08-12 사용자 요구).
 *
 * ★ 2026-08-12 (사용자 요구) — 맨 왼쪽 "상태"도 **드롭다운**이다: 누르면 출근·회의중·자리비움·
 *   외근·퇴근을 고를 수 있고, 고른 상태가 그 자리에 그대로 표시된다. 종전에는 점 하나 + 글자뿐인
 *   장식이었다. 출근/퇴근은 서버 근태 기록(`/hr/attendance/*`)까지 남고 나머지는 로컬 표시 상태다
 *   (상세 근거: `lib/work-status.ts`).
 *
 * 세션 시계(2026-08-11): 우측 레일 프로필 아래에 있던 시계를 **상태바 맨 오른쪽**으로 옮겼다.
 * 스탑워치 그림 없이 남은 시간 텍스트만 둔다. `useSessionIdle` 은 구독 카운트 기반 싱글턴이라
 * AppLayout 의 SessionIdleGuard 와 함께 구독해도 타이머는 하나만 돈다.
 * ⚠ 퀵바 Ctrl+Space 는 CJK IME 한/영 토글과 충돌 가능 — 실제 바인딩은 후속(§5-⑥).
 */

/** 상태바 항목 ↔ 메뉴 레지스트리 menu_key 매핑. 전자결재는 레지스트리에 행을 만들면 켜진다. */
const CHAT_MENU_KEY = 'chat';
const MAIL_MENU_KEY = 'mail';
const APPROVAL_MENU_KEY = 'approval';
/** 위 세 항목은 상태바가 **직접** 그린다(드롭다운·배지 등 전용 동작이 붙어 있어서). */
const BUILTIN_KEYS = new Set([CHAT_MENU_KEY, MAIL_MENU_KEY, APPROVAL_MENU_KEY]);

/**
 * 관리자가 `section='status'` 로 등록한 메뉴를 상태바에 그릴 때 쓰는 아이콘 화이트리스트.
 * NavBar 의 ICON_BY_NAME 과 같은 이름 집합을 인식한다(관리 콘솔 아이콘 피커와 정합).
 * 'custom:*' 은 SVG 본문을 그대로 렌더하고, 모르는 이름은 점(CircleIcon)으로 떨어뜨린다.
 */
const STATUS_ICON_BY_NAME: Record<string, LucideIcon> = {
  LayoutGrid, MessageCircle, Mail, Calendar, Target, Folder, BookOpen, Settings, Network, FileText,
};

/**
 * 상태바 맨 오른쪽 세션 시간 — 남은 세션 유지 시간(mm:ss)만 텍스트로.
 * 만기 임박 상태는 색으로만 알린다(warn/danger) — 아이콘 없이 폭을 최소로 유지.
 */
function SessionTime() {
  const { remaining, state } = useSessionIdle();
  const time = formatSessionTime(remaining);
  const expired = state === 'expired';
  return (
    <span
      className={`status-bar__item status-bar__session is-${state}`}
      role="timer"
      aria-live="off"
      title={tLayout('layout.session.title', { time })}
    >
      {expired ? tLayout('layout.session.expired') : time}
    </span>
  );
}

/**
 * 맨 왼쪽 근무 상태 칩 — 누르면 위로 상태 목록이 열린다(2026-08-12).
 *
 * 미설정이면 종전과 같은 "상태" 글자를 그대로 쓴다(빈 화면처럼 보이지 않게). 상태를 고르면
 * 그 라벨로 바뀌고 점 색이 상태 색을 띤다. 서버 근태 조회는 mount 1회 best-effort —
 * 실패해도 로컬 표시 상태로 degrade 한다(스토어 `hydrate` 가 흡수).
 */
function WorkStatusItem() {
  const [open, setOpen] = useState(false);
  const status = useWorkStatusStore((s) => s.status);
  const hydrate = useWorkStatusStore((s) => s.hydrate);

  useEffect(() => { void hydrate(); }, [hydrate]);

  const label = status ? statusLabel(status) : tLayout('layout.workstatus.none');
  const tone = status ? WORK_STATUS_TONE[status] : 'none';

  return (
    <div className="status-bar__anchor">
      <button
        type="button"
        className={`status-bar__item status-bar__status is-${tone}`}
        aria-label={status
          ? tLayout('layout.workstatus.aria_current', { name: label })
          : tLayout('layout.workstatus.aria')}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <CircleIcon size={9} aria-hidden />
        <span>{label}</span>
      </button>
      <StatusDropdown open={open} onClose={() => setOpen(false)} placement="up" />
    </div>
  );
}

export function StatusBar() {
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const notifUnread = useNotificationStore((s) => s.unreadCount);
  const chatUnread = useChatStore((s) => s.chatUnreadTotal);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  // ⑤ 메모 전역 진입점(design-20260724 §4.3 / 결정 D-1) — ⑤ 레일은 캘린더만 opt-in 이라
  // 상태바가 전 화면에서 상시 보이는 진입점을 담당한다.
  const toggleMemo = useMemoPanelStore((s) => s.togglePanel);
  const memoOpen = useMemoPanelStore((s) => s.open);
  // 관리 콘솔 메뉴 레지스트리 — NavBar 와 같은 스냅샷(lib/menu-config 싱글턴).
  const { rows } = useMenuConfig();

  const chatMenu = findMenu(rows, CHAT_MENU_KEY);
  const mailMenu = findMenu(rows, MAIL_MENU_KEY);
  const approvalMenu = findMenu(rows, APPROVAL_MENU_KEY);
  const chatLabel = chatMenu?.label ?? tLayout('layout.nav.chat');
  const mailLabel = mailMenu?.label ?? tLayout('layout.nav.mail');
  const approvalLabel = approvalMenu?.label ?? tLayout('layout.nav.approval');

  // 방 선택 — 활성 방을 store 에 심고 채팅 화면으로. ChatPage 가 activeChatId 로 대화를 연다
  // (대시보드 '최근 채팅' 위젯과 같은 진입 방식).
  const openRoom = (roomId: string) => {
    setActiveChat(roomId);
    navigate(chatMenu?.url ?? '/chat');
  };

  return (
    <footer className="status-bar" role="contentinfo" aria-label="상태 표시줄">
      <WorkStatusItem />

      {isMenuVisible(rows, CHAT_MENU_KEY) && (
        <div className="status-bar__anchor">
          <button
            type="button"
            className="status-bar__item"
            aria-label={chatUnread > 0 ? tLayout('layout.status.aria_chat_count', { n: chatUnread }) : chatLabel}
            aria-expanded={chatOpen}
            onClick={() => setChatOpen((v) => !v)}
          >
            <MessageCircle size={13} aria-hidden />
            <span>{chatLabel}</span>
            {chatUnread > 0 && <span className="status-bar__badge">{chatUnread > 99 ? '99+' : chatUnread}</span>}
          </button>
          <ChatDropdown
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            onOpenRoom={openRoom}
            onViewAll={() => navigate(chatMenu?.url ?? '/chat')}
            placement="up"
          />
        </div>
      )}

      {isMenuVisible(rows, MAIL_MENU_KEY) && (
        <button
          type="button"
          className="status-bar__item"
          aria-label={mailLabel}
          onClick={() => navigate(mailMenu?.url ?? '/mail')}
        >
          <Mail size={13} aria-hidden />
          <span>{mailLabel}</span>
        </button>
      )}

      <div className="status-bar__notif">
        <button
          type="button"
          className="status-bar__item"
          aria-label={notifUnread > 0 ? `알림 ${notifUnread}건` : '알림'}
          aria-expanded={notifOpen}
          onClick={() => setNotifOpen((v) => !v)}
        >
          <Bell size={13} aria-hidden />
          <span>알림</span>
          {notifUnread > 0 && <span className="status-bar__badge">{notifUnread > 99 ? '99+' : notifUnread}</span>}
        </button>
        {/*
          2026-08-18 — 좁은 드롭다운(안읽음 8건 + '전체 보기' 로 페이지 이동) 대신 팝업.
          사용자 요구: "내 프로필의 내 알림으로 이동하지 말고 팝업으로 오픈한다."
          좌측 분류(전체·안읽음·메뉴별) + 우측 목록 + '설정 및 관리'(알림설정 모달).
        */}
        <NotificationPanel
          open={notifOpen}
          onClose={() => setNotifOpen(false)}
          placement="up"
        />
      </div>

      {MEMO_PANEL_ENABLED && (
        <button
          type="button"
          className={`status-bar__item${memoOpen ? ' is-on' : ''}`}
          aria-label={tMemo('memo.open')}
          aria-pressed={memoOpen}
          onClick={toggleMemo}
        >
          <StickyNote size={13} aria-hidden />
          <span>{tMemo('memo.title')}</span>
        </button>
      )}

      {/* 전자결재 — 레지스트리에 'approval' 행이 등록됐을 때만 노출한다(관리 콘솔 기준).
          행이 없으면 라우트도 미노출(NavBar 정책)이라 상태바에서도 감춘다. */}
      {approvalMenu && (
        <button
          type="button"
          className="status-bar__item"
          aria-label={approvalLabel}
          onClick={() => navigate(approvalMenu.url)}
        >
          <FileText size={13} aria-hidden />
          <span>{approvalLabel}</span>
        </button>
      )}

      {/* 관리자가 "메뉴 관리"에서 위치를 **하단(상태바)** 로 지정한 메뉴들(section='status').
          좌측 레일에는 뜨지 않고 여기에만 온다(NavBar.rowsToCfg 가 제외). 전용 동작이 붙은
          내장 항목(채팅·메일·전자결재)은 위에서 이미 그렸으므로 건너뛴다. */}
      {(rows ?? [])
        .filter((m) => m.section === 'status' && !BUILTIN_KEYS.has(m.menu_key))
        .map((m) => {
          const Icon = STATUS_ICON_BY_NAME[m.icon] ?? CircleIcon;
          const inner = (
            <>
              {m.svg
                ? <span className="status-bar__svgicon" aria-hidden dangerouslySetInnerHTML={{ __html: m.svg }} />
                : <Icon size={13} aria-hidden />}
              <span>{m.label}</span>
            </>
          );
          return m.is_external ? (
            <a
              key={m.menu_key}
              className="status-bar__item"
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={m.label}
            >
              {inner}
            </a>
          ) : (
            <button
              key={m.menu_key}
              type="button"
              className="status-bar__item"
              aria-label={m.label}
              onClick={() => navigate(m.url)}
            >
              {inner}
            </button>
          );
        })}

      <div className="status-bar__center">
        <button type="button" className="status-bar__quickbar" title="퀵바 메뉴">
          <CircleIcon size={10} aria-hidden />
          <span>퀵바 메뉴</span>
        </button>
      </div>

      <SessionTime />
    </footer>
  );
}
